import { Injectable } from '@angular/core';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { MessageService } from '../service-ui/message.service';
import { HttpClient } from '@angular/common/http';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import { StartupService } from '../service-ui/startup.service';
import {environment} from "../../environments/environment";
import {Order} from "../../model/Order";
import {DataService} from "./data-service";
import {retryTransient} from "./http-error";
import {OrderOption} from "../../model/cart/OrderOption";
import {PackageAndShippingComputationService} from "./package-and-shipping-computation.service";
import {Item} from "../../model/cart/Item";
import {ShippingItem} from "../../model/cart/ShippingItem";
import {Address} from "../../model/Address";
import {ProductionRateTable} from "../../model/cart/ProductionRateTable";
import {Cart} from "../../model/cart/Cart";
import {OrderDeliverTo} from "../../model/OrderDeliverTo";
import {OrderItem} from "../../model/OrderItem";
import {PrintNodeOrder} from "../../model/PrintNodeOrder";
import {PrintNodeOrderItem} from "../../model/PrintNodeOrderItem";
import {OrderDeliverToService} from "./order-deliver-to.service";
import {PrintNodeOrderService} from "./print-node-order.service";
import {ShippingOption} from "../../model/cart/ShippingOption";

@Injectable()
export class OrderService extends DataService {
  private objects: Order[] = [];
  private objectsObservable = of( this.objects );
  public loadingState = 'INITIAL';

  /**
   * Per-item cache of production-rate tables. Keyed by a composite
   * `<ClassName>#<Item.getId()>` string (see
   * {@link productionRateCacheKey}) so `ProductItem` and `DraftItem`
   * instances that share a numeric id don't collide. Survives cart
   * clearing so that re-adding an item doesn't re-fetch rates.
   */
  private productionRateCache: Map<string, ProductionRateTable> = new Map<string, ProductionRateTable>();

  constructor(private http: HttpClient, private messageService: MessageService, private startupService: StartupService,
              private packageAndShippingComputationService: PackageAndShippingComputationService,
              private orderDeliverToService: OrderDeliverToService,
              private printNodeOrderService: PrintNodeOrderService) {
    super(startupService);
  }

    reset (filter: string = '') {
    this.loadingState = 'INITIAL';
    while ( this.objects.length > 0) {
      this.objects.pop();
    }
  }

  getOrders(): Observable<Order[]> {
    if ( this.loadingState === 'INITIAL') {
      this.loadingState = 'LOADING';
      const url = `${environment.apiBaseURL}order`;
      this.messageService.add('OrderService: fetched orders');
      return this.http.get<Order[]>(url, this.startupService.getHttpOptions())
        .pipe(
          map(orders => {
            while ( this.objects.length > 0) {
              this.objects.pop();
            }
            for ( const order of orders ) {
              const o = new Order(order);
              this.objects.push(o);
            }
            this.loadingState = 'LOADED';
            return this.objects;
          }), catchError(this.handleError('getOrders', []))
        );
    } else {
      return this.objectsObservable;
    }
  }


  getOrder(id: string): Observable<Order> {
    const url = `${environment.apiBaseURL}order/${id}`;
    this.messageService.add(`OrderService: fetched order id=${id}`);
    return this.http.get<Order>(url, this.startupService.getHttpOptions())
      .pipe(
        map(order => {
          const p = new Order(order);
          return p;
        }),
        catchError(this.handleError<Order>(`getOrder id=${id}`))
    );
  }

  /** PUT: update the order on the server */
  updateOrder (order: Order): Observable<Order> {
    const url = `${environment.apiBaseURL}order/${order.ID}`;
    this.messageService.add(`OrderService: updated order id=${order.ID}`);
    return this.http.put(url, order, this.startupService.getHttpOptions()).pipe(
      tap(_ => this.log(`updated order id=${order.ID}`)),
      catchError(this.handleError<any>('updateOrder'))
    );
  }

  /** PUT: update the order on the server */
  updateDebitLineEntries (order: Order): Observable<Order | undefined> {
    const url = `${environment.apiBaseURL}order/updateDebitLineEntries/${order.ID}`;
    this.messageService.add(`OrderService: updateDebitLineEntries id=${order.ID}`);
    return this.http.get<Order>(url, this.startupService.getHttpOptions())
      .pipe(
        map(o => {
          const x = new Order(o);
          return x;
        }),
        catchError(this.handleError<Order>(`getOrder id=${order.ID}`))
      );
  }

  /**
   * Create an Order.
   *
   * Retry scoped to transient failures (PF-13). It was `retry(6)`, which
   * re-sent a rejected order seven times: the server had already decided, so
   * every extra attempt was load with no chance of a different answer, and it
   * delayed the error by however long six round trips took.
   *
   * Failure still yields `undefined` rather than erroring, because all four
   * callers test `if (order && order.ID)`. Changing that here would break them
   * silently, so the contract is left alone -- the callers that need the
   * REASON (the replenishment screens) get it from the OrderDeliverTo call,
   * which is the one that carries the server's validation messages.
   */
  addOrder (order: Order): Observable<Order> {
    const url = `${environment.apiBaseURL}order`;
    return this.http.post<Order>(url, order, this.startupService.getHttpOptions()).pipe(
      retryTransient<Order>(1, 500),
      map(o => {
        this.log(`added order w/ id=${order.ID}`);
        const x = new Order(o);
        this.objects.push(x);
        return x;
      }),
      catchError(this.handleError<Order>('addOrder'))
    );
  }

  /** DELETE: delete the order from the server */
  deleteOrder (order: Order): Observable<Order> {
    const url = `${environment.apiBaseURL}order/${order.ID}`;

    return this.http.delete<Order>(url, this.startupService.getHttpOptions()).pipe(
      tap(_ => this.log(`deleted order id=${order.ID}`)),
      catchError(this.handleError<Order>('deleteOrder'))
    );
  }

  /**
   * Get production rates for an OrderOption.
   * Returns item-level charges and weights needed to support the Cart interface.
   */
  getProductionRates(orderOption: OrderOption): Observable<any> {
    const url = `${environment.apiBaseURL}order/productionrates`;
    const items = [];
    for (const pnoo of orderOption.printNodeOrderOptions) {
      for (const item of pnoo.items) {
        items.push({
          id: item.getId(),
          quantity: item.quantity,
          pages: item.getPages(),
          interiorType: item.getInteriorType(),
          bindingType: item.getBindingType(),
          laminateType: item.getLaminateType(),
          width: item.getWidth(),
          height: item.getHeight(),
          weight: item.getWeight(),
          thickness: item.getThickness(),
          paperType: item.getPaperType(),
          caseQuantity: item.getCaseQuantity(),
          useInventory: item.useInventory,
          inventoryFacilityId: item.inventoryFacilityId
        });
      }
    }
    const payload = {
      carrier: orderOption.carrier,
      serviceLevel: orderOption.serviceLevel,
      strategy: orderOption.strategy,
      packItemsInSeparateBoxes: orderOption.packItemsInSeparateBoxes,
      useCaseQuantities: orderOption.useCaseQuantities,
      items: items
    };
    this.messageService.add('OrderService: getProductionRates');
    return this.http.post<any>(url, payload, this.startupService.getHttpOptions()).pipe(
      map(response => response),
      catchError(this.handleError<any>('getProductionRates'))
    );
  }

  /**
   * Compute packages and shipping for an OrderOption.
   * Calls package-and-shipping-computation service for each PrintNodeOrderOption.
   */
  computePackagesAndShipping(orderOption: OrderOption, address: Address): void {
    for (const pnoo of orderOption.printNodeOrderOptions) {
      const shippingItems: ShippingItem[] = pnoo.getShippingItems();
      this.packageAndShippingComputationService.getPackages(
        pnoo.carrier, pnoo.serviceLevel, pnoo.facilityId, shippingItems,
        pnoo.packItemsInSeparateBoxes, pnoo.useCaseQuantities, pnoo.computationVersion
      ).subscribe(packagesResponse => {
        if (packagesResponse && packagesResponse['status'] === 'SUCCESS' && packagesResponse['packages']) {
          pnoo.shippingPackages = [];
          for (const pkg of packagesResponse['packages']) {
            const sp = { height: pkg.height, width: pkg.width, depth: pkg.depth, weight: pkg.weight, quantity: pkg.quantity, shippingOptions: [] };
            pnoo.shippingPackages.push(sp as any);
            this.packageAndShippingComputationService.getShippingOptions(
              pnoo.carrier, pnoo.serviceLevel, pnoo.facilityId, address, sp as any, pnoo.computationVersion
            ).subscribe(shippingResponse => {
              if (shippingResponse && shippingResponse['status'] === 'SUCCESS') {
                // Materialise the wire objects as full `ShippingOption`
                // instances so downstream code that calls methods on
                // them (notably `Cart.getUnifiedShippingChoices`, which
                // invokes `match.getDeliveryDaysRange()`) doesn't throw
                // `is not a function` on plain JSON objects — that
                // exception silently collapsed `getUnifiedShippingChoices`
                // to `[]` and the shipping picker rendered no options
                // and no costs even though the server had returned them.
                const rawOptions = shippingResponse['shippingOptions'] || [];
                (sp as any).shippingOptions = rawOptions.map((o: any) => new ShippingOption(
                  o.carrier,
                  o.serviceLevel,
                  o.cost,
                  o.deliveryDays,
                  o.signature,
                  o.packageSetId,
                  o.deliveryDaysMin,
                  o.deliveryDaysMax
                ));
                (sp as any).computationStatus = 'COMPLETE';
              }
            });
          }
          pnoo.computationStatus = 'COMPLETE';
        }
      });
    }
  }

  /**
   * Get production rates for a single Item, returning a ProductionRateTable that
   * lists per-facility quantity-break pricing. Results are cached by item id;
   * subsequent calls for the same item return the cached table without a server hit.
   *
   * Cache key is namespaced by the item's runtime class name
   * (`ProductItem` / `DraftItem`) so a Draft with the same numeric ID
   * as a Product doesn't collide on the same cache entry — the two
   * types live in separate DB tables and share the ID space.
   *
   * Payload construction is wrapped in a try/catch: a Draft whose
   * `draftBody` / `draftCover` hasn't finished loading via the
   * DraftService's async link step used to throw a null-ref inside
   * an accessor (e.g. `DraftItem.getPages()`) — that threw AFTER the
   * status was flipped to `LOADING`, leaving the item stuck in the
   * spinner forever with no observable emit to advance it. The
   * DraftItem accessors are now defensive (return 0 / '' when the
   * async data isn't loaded yet) but this catch keeps the pipeline
   * sound against any future accessor that regresses.
   */
  getProductionRatesForItem(item: Item): Observable<ProductionRateTable> {
    const itemId = item.getId();
    const cacheKey = this.productionRateCacheKey(item);
    const cached = this.productionRateCache.get(cacheKey);
    if (cached) {
      item.productionRates = cached;
      item.productionRatesStatus = 'READY';
      return of(cached);
    }
    // Gate the request on the item having all rate-input fields ready
    // from data actually attached on the client. A DraftItem whose
    // `draftBody` / `draftCover` haven't finished linking (via
    // DraftService.getLinkData → DraftBodyService/DraftCoverService)
    // would send `pages/width/height/paperType/laminateType` as
    // 0/'' — the rate engine returns an empty table for those inputs
    // and we'd cache that empty table as `READY`, permanently masking
    // the real rates. Leave status at `NONE` so a later trigger (the
    // cart-page subscribes to DraftBodyService/DraftCoverService
    // add-notifiers and re-fires) resolves it once the inputs land.
    if (typeof (item as any).hasCompleteRateInputs === 'function'
        && !(item as any).hasCompleteRateInputs()) {
      if (!environment.production) {
        console.log(`[OrderService] deferring production-rate fetch for item ${itemId} — rate inputs not yet complete (linked draftBody / draftCover)`);
      }
      item.productionRatesStatus = 'NONE';
      return of(new ProductionRateTable({ itemId }));
    }
    item.productionRatesStatus = 'LOADING';
    const url = `${environment.apiBaseURL}order/productionrates/item`;
    let payload: any;
    try {
      payload = {
        id: itemId,
        pages: item.getPages(),
        interiorType: item.getInteriorType(),
        bindingType: item.getBindingType(),
        laminateType: item.getLaminateType(),
        width: item.getWidth(),
        height: item.getHeight(),
        weight: item.getWeight(),
        thickness: item.getThickness(),
        paperType: item.getPaperType(),
        caseQuantity: item.getCaseQuantity()
      };
    } catch (payloadErr) {
      // An Item accessor threw (typically a Draft whose linked body /
      // cover hasn't arrived yet). Surface as ERROR so the cart-page
      // template renders the recoverable "Price unavailable — Retry"
      // block instead of showing a stale spinner indefinitely.
      console.error('getProductionRatesForItem payload construction failed', payloadErr, item);
      item.productionRatesStatus = 'ERROR';
      return of(new ProductionRateTable({ itemId }));
    }
    this.messageService.add(`OrderService: getProductionRatesForItem id=${itemId}`);
    return this.http.post<any>(url, payload, this.startupService.getHttpOptions()).pipe(
      map(response => {
        const table = new ProductionRateTable(response || { itemId });
        if (!table.itemId) { table.itemId = itemId; }
        this.productionRateCache.set(cacheKey, table);
        item.productionRates = table;
        item.productionRatesStatus = 'READY';
        return table;
      }),
      catchError(err => {
        item.productionRatesStatus = 'ERROR';
        console.error('getProductionRatesForItem failed', err);
        this.log(`getProductionRatesForItem id=${itemId} failed: ${err && err.message}`);
        return of(new ProductionRateTable({ itemId }));
      })
    );
  }

  /**
   * Cache key for the per-item production-rate table. Namespaced by
   * the item's runtime class name so `ProductItem #17` and
   * `DraftItem #17` (which live in different DB tables that share
   * ID space) don't collide on the same entry. Falls back to the
   * numeric id alone for any pre-existing item type without a
   * distinct constructor name.
   */
  private productionRateCacheKey(item: Item): string {
    const typeName = item && item.constructor && item.constructor.name
      ? item.constructor.name
      : 'Item';
    return `${typeName}#${item.getId()}`;
  }

  /**
   * Clear the production-rate cache. With no argument, clears everything;
   * with an itemId, clears every entry for that id across item types
   * (typical: quantity/rate stale after a book metadata change; the caller
   * doesn't know or care whether it was a Product or a Draft).
   * Useful when book metadata changes (e.g., page count, binding) and
   * rates need to be refetched.
   */
  clearProductionRateCache(itemId?: number) {
    if (itemId === undefined || itemId === null) {
      this.productionRateCache.clear();
    } else {
      // Remove every namespaced entry whose id-suffix matches. Cheap
      // — the cache has at most one entry per (type, id) pair.
      const suffix = '#' + itemId;
      for (const key of Array.from(this.productionRateCache.keys())) {
        if (key === String(itemId) || key.endsWith(suffix)) {
          this.productionRateCache.delete(key);
        }
      }
    }
  }

  // ──────────────────────────────────────────────────────────────────────
  // Cart redesign — new server endpoints (S-2, S-3, S-6).
  // See docs/contracts/shared/{GetFacilities,RateShipping,PlaceOrder}.md.
  // ──────────────────────────────────────────────────────────────────────

  /**
   * Fetch the facility candidates that can fulfil the cart's items to the
   * cart's delivery address. Replaces the legacy hardcoded ZIP-prefix
   * facility routing in `Cart.compute()` once
   * `environment.useNewCartShippingFlow` is flipped on.
   *
   * The server is the canonical decider — it caps to the top 3 candidates,
   * sets `splitRequired` per facility, and returns a `signature` the cart
   * echoes into the `rateShipping` and `placeOrder` envelopes.
   *
   * Populates `cart.facilityCandidates`, `cart.facilitySignature`, and
   * `cart.splitOrderRequired` as a side effect; also resolves with the
   * raw response.
   *
   * The non-zero items list is built from `cart.items` (where
   * `item.quantity > 0`). `cart.address` provides the delivery address.
   * `cart.inventoryType` and the caller's selected org id are wired
   * automatically.
   */
  getFacilitiesForCart(cart: Cart): Observable<any> {
    if (!cart || !cart.address) {
      return throwError(() => new Error('getFacilitiesForCart: cart or address missing'));
    }
    const orgId = Number(this.startupService.selectedRoleId);
    const nonZero = (cart.items || []).filter(i => i && i.quantity > 0);
    if (nonZero.length === 0) {
      return throwError(() => new Error('getFacilitiesForCart: cart has no non-zero items'));
    }
    const a = cart.address;
    const payload = {
      orgId,
      inventoryType: cart.inventoryType,
      deliveryAddress: {
        name: (a.firstname || '') + ((a.firstname && a.lastname) ? ' ' : '') + (a.lastname || ''),
        address1: a.address1 || '',
        address2: a.address2 || '',
        city: a.city || '',
        state: a.state || '',
        postalCode: a.postalcode || '',     // server contract documents both forms
        postalcode: a.postalcode || '',
        country: a.country || 'US'
      },
      items: nonZero.map(item => ({
        productId: item.getId(),
        quantity: item.quantity,
        pages: item.getPages(),
        weight: item.getWeight(),
        bindingType: item.getBindingType(),
        interiorType: item.getInteriorType(),
        paperType: item.getPaperType(),
        laminateType: item.getLaminateType()
      }))
    };
    const url = `${environment.apiBaseURL}order/getfacilities`;
    cart.facilityStatus = 'COMPUTING';
    this.messageService.add(`OrderService: getFacilitiesForCart org=${orgId} items=${nonZero.length}`);
    return this.http.post<any>(url, payload, this.startupService.getHttpOptions()).pipe(
      map(response => {
        if (!response) {
          cart.facilityStatus = 'ERROR';
          return undefined;
        }
        cart.facilityCandidates = response.facilities || [];
        cart.facilitySignature = response.signature || '';
        // splitOrderRequired is true when none of the candidates can produce
        // every item — i.e. every candidate has splitRequired=true.
        cart.splitOrderRequired = cart.facilityCandidates.length > 0
          && cart.facilityCandidates.every(f => f.splitRequired === true);
        cart.facilityStatus = 'READY';
        return response;
      }),
      catchError(err => {
        cart.facilityStatus = 'ERROR';
        console.error('getFacilitiesForCart failed', err);
        this.log(`getFacilitiesForCart failed: ${err && err.message}`);
        return throwError(() => err);
      })
    );
  }

  /**
   * Fetch the combined package plan + carrier/serviceLevel rates for every
   * candidate facility. Replaces the legacy two-call
   * `getPackages` + `getShippingOptions` flow once
   * `environment.useNewCartShippingFlow` is flipped on.
   *
   * Requires `cart.facilityCandidates` to be populated; pass the cart that
   * was just returned from `getFacilitiesForCart` so the
   * `facilitySignature` round-trips and lets the server skip the
   * facility-recompute fast-path.
   *
   * Populates `cart.rateShippingByFacility` and `cart.rateShippingSignature`
   * as a side effect; resolves with the raw response.
   */
  rateShippingForCart(cart: Cart): Observable<any> {
    if (!cart || !cart.address) {
      return throwError(() => new Error('rateShippingForCart: cart or address missing'));
    }
    if (!cart.facilityCandidates || cart.facilityCandidates.length === 0) {
      return throwError(() => new Error('rateShippingForCart: cart.facilityCandidates is empty (call getFacilitiesForCart first)'));
    }
    const orgId = Number(this.startupService.selectedRoleId);
    const nonZero = (cart.items || []).filter(i => i && i.quantity > 0);
    if (nonZero.length === 0) {
      return throwError(() => new Error('rateShippingForCart: cart has no non-zero items'));
    }
    const a = cart.address;
    const payload = {
      orgId,
      inventoryType: cart.inventoryType,
      deliveryAddress: {
        name: (a.firstname || '') + ((a.firstname && a.lastname) ? ' ' : '') + (a.lastname || ''),
        address1: a.address1 || '',
        address2: a.address2 || '',
        city: a.city || '',
        state: a.state || '',
        postalCode: a.postalcode || '',
        postalcode: a.postalcode || '',
        country: a.country || 'US'
      },
      items: nonZero.map(item => ({
        productId: item.getId(),
        quantity: item.quantity,
        pages: item.getPages(),
        weight: item.getWeight(),
        bindingType: item.getBindingType(),
        interiorType: item.getInteriorType(),
        paperType: item.getPaperType(),
        laminateType: item.getLaminateType()
      })),
      facilities: cart.facilityCandidates.map(f => ({ facilityId: f.facilityId })),
      facilitySignature: cart.facilitySignature || undefined
    };
    const url = `${environment.apiBaseURL}order/rateshipping`;
    cart.shippingStatus = 'COMPUTING';
    this.messageService.add(`OrderService: rateShippingForCart org=${orgId} facilities=${cart.facilityCandidates.length}`);
    return this.http.post<any>(url, payload, this.startupService.getHttpOptions()).pipe(
      map(response => {
        if (!response) {
          cart.shippingStatus = 'ERROR';
          return undefined;
        }
        const byFacility: { [facilityId: number]: any } = {};
        for (const perFac of (response.perFacility || [])) {
          byFacility[perFac.facilityId] = {
            packageSets: perFac.packageSets || [],
            rates: perFac.rates || []
          };
        }
        cart.rateShippingByFacility = byFacility;
        cart.rateShippingSignature = response.signature || '';
        // Status stays at COMPUTING until the user (or auto-select) picks a
        // (facility, carrier, serviceLevel). cart-page transitions it to
        // READY once selectedShippingRateSignature is set.
        return response;
      }),
      catchError(err => {
        cart.shippingStatus = 'ERROR';
        console.error('rateShippingForCart failed', err);
        this.log(`rateShippingForCart failed: ${err && err.message}`);
        return throwError(() => err);
      })
    );
  }

  /**
   * Build the single-round-trip place-order envelope documented in
   * `docs/architecture/order-placement.md` §4.1 and mirrored on the
   * shared contract `docs/contracts/shared/PlaceOrder.md`.
   *
   * The envelope is a self-contained, server-verifiable snapshot of
   * everything the cart has computed:
   *
   *   - `order` — top-level `Order` fields (org, payer, PO refs,
   *     charge-profile hint, and the client's current view of the
   *     top-line dollar totals so the server can flag mismatches).
   *   - `orderDeliverTos[]` — exactly one entry from the UI; carries
   *     the recipient address + `orderItems[]` (one per distinct
   *     product with summed quantities across the cart).
   *   - `printNodeOrders[]` — one entry per selected
   *     `PrintNodeOrderOption` (i.e. per ship-from facility). Each
   *     entry carries its own `printNodeOrder` shell, its
   *     `printNodeOrderItems[]` (items assigned to that facility)
   *     and `packages[]` (one entry per physical box, each with its
   *     nested `orderItemToPackages[]` distribution).
   *   - `payment` — routing hint the server uses to decide which
   *     charge path to run (invoice / saved profile / new card).
   *   - `signatures` — the server-issued opaque signatures the cart
   *     accumulated during rate + tax computation. If any signature
   *     is missing / stale, the server recomputes the underlying
   *     component and either matches the client value (accept) or
   *     returns `SIGNATURE_MISMATCH` (client re-fetches, user
   *     reviews new prices, resubmits).
   *   - `clientRequestId` — UUID for server-side dedupe / replay.
   *
   * Single caller today: {@link placeOrderViaEnvelope}. The server
   * `payload.dryRun` field was retired on 2026-07-23 (server rejects
   * `dryRun: true` with HTTP 400 for the 90-day tripwire window, then
   * silently ignores it after that), so the builder no longer emits
   * a `dryRun` key and the historical `placeOrderDryRun(cart)` entry
   * point has been removed.
   */
  buildPlaceOrderEnvelope(cart: Cart): any {
    const clientRequestId = this.generateClientRequestId();
    const oo = cart.selectedOrderOption;
    const pnooList = (oo && oo.printNodeOrderOptions) ? oo.printNodeOrderOptions : [];

    // ── order ──────────────────────────────────────────────
    const nonZeroCartItems = (cart.items || []).filter(i => i && i.quantity > 0);
    let totalQty = 0;
    for (const ci of nonZeroCartItems) { totalQty += ci.quantity; }
    // Sales-tax breakdown: use the per-bucket values the server
    // already returned on `cart.salesTaxResult` rather than
    // re-deriving them via a cost-proportional split of
    // `cart.taxCost`. Jurisdictions frequently tax only some
    // cost buckets (e.g. shipping-only-taxable states) and
    // exempt others, so the server-authoritative breakdown is
    // the only correct source. If for some reason the breakdown
    // isn't available (very old cached response, or the server
    // returned a scalar we've flattened to `taxCost`), fall back
    // to the pre-tax proportional split so the envelope still
    // balances to `sum(...TaxCharge) === taxCost` — the server
    // will reject anything else with `SIGNATURE_MISMATCH`.
    const tax = cart.salesTaxResult;
    const preTax = (cart.itemsCost || 0) + (cart.shippingCost || 0) + (cart.packingAndPackagingCost || 0);
    const proportionalTax = (bucket: number): number => {
      if (!cart.taxCost || preTax <= 0) { return 0; }
      return (cart.taxCost * bucket) / preTax;
    };
    const hasBreakdown = !!tax
      && (tax.manufacturingTax || tax.packingTax || tax.shippingTax
          || tax.gatewayTax || tax.softproofTax) !== 0;
    // Roll gateway + softproof (rare, not carried on the Order
    // row as separate columns) into the manufacturing bucket so
    // the envelope's three tax fields still sum to
    // `salesTaxResult.totalTax === cart.taxCost`.
    const manufacturingTaxCharge = hasBreakdown
      ? (tax.manufacturingTax || 0) + (tax.gatewayTax || 0) + (tax.softproofTax || 0)
      : proportionalTax(cart.itemsCost || 0);
    const shippingTaxCharge = hasBreakdown
      ? (tax.shippingTax || 0)
      : proportionalTax(cart.shippingCost || 0);
    const packingTaxCharge = hasBreakdown
      ? (tax.packingTax || 0)
      : proportionalTax(cart.packingAndPackagingCost || 0);
    const d = new Date();
    const generatedCustomerOrderId = `Order-${d.getMonth() + 1}.${d.getDate()}.${d.getFullYear()}.${Math.floor(Math.random() * 1000)}`;
    const orderPayload: any = {
      customerorderid: generatedCustomerOrderId,
      org_ID: Number(this.startupService.selectedRoleId),
      inventoryType: cart.inventoryType,
      payer: cart.payer,
      productionMethod: cart.inventoryType || 'PRINT_TO_ORDER',
      purchaseOrder: cart.purchaseOrder || '',
      purchaseOrder_private: cart.purchaseOrderPrivate || '',
      chargeprofile_ID: (cart.chargeProfile && cart.chargeProfile.ID > 0) ? cart.chargeProfile.ID : 0,
      // Client's view of the top-line totals — server recomputes and
      // may adjust before persisting. See `order-placement.md` §4.2.
      manufacturingCharge: cart.itemsCost || 0,
      shippingCharge: cart.shippingCost || 0,
      packingCharge: cart.packingAndPackagingCost || 0,
      manufacturingTaxCharge: manufacturingTaxCharge,
      shippingTaxCharge: shippingTaxCharge,
      packingTaxCharge: packingTaxCharge,
      quantity: totalQty,
      shipmentsTotal: pnooList.length || 1
    };

    // ── orderDeliverTos + orderItems ───────────────────────
    // Single delivery address from the UI; N supported by the API.
    const a = cart.address;
    const orderItemsPayload: any[] = [];
    const byProductId: { [id: number]: any } = {};
    for (const cartItem of nonZeroCartItems) {
      const productId = cartItem.getId();
      let oi = byProductId[productId];
      if (!oi) {
        oi = {
          product_ID: productId,
          quantity: 0,
          cost: 0,
          weight: (cartItem.getWeight && cartItem.getWeight()) || 0
        };
        byProductId[productId] = oi;
        orderItemsPayload.push(oi);
      }
      oi.quantity += cartItem.quantity;
      oi.cost = (oi.cost || 0) + (cartItem.cost || 0);
    }
    const orderDeliverTosPayload = [{
      orderDeliverTo: {
        title: '',
        firstname: (a.firstname || '').toUpperCase(),
        lastname: (a.lastname || '').toUpperCase(),
        company: (a.company || '').toUpperCase(),
        address1: a.address1 || '',
        address2: a.address2 || '',
        city: a.city || '',
        state: a.state || '',
        postalcode: a.postalcode || '',
        country: a.country || '',
        phone: cart.phone || '',
        phonecountry: cart.phoneCountry || '',
        email: a.email || '',
        carrier: cart.selectedCarrier || '',
        servicelevel: cart.selectedServiceLevel || '',
        addressValidationStatus: cart.addressStatus === 'VALIDATED' ? 'VALID'
          : cart.addressStatus === 'MANUAL_OVERRIDE' ? 'MANUAL_OVERRIDE' : 'PENDING',
        inventoryType: cart.inventoryType,
        // Per-delivery charge/tax totals. The server cross-checks
        // that the sum of these six fields across all
        // orderDeliverTos[] equals the order-level sum within a
        // $0.02 epsilon (see inbox/_processed/
        // 2026-07-22-placeorder-server-authoritative-amount.md).
        // With a single ODT the per-delivery totals equal the
        // order-level totals exactly.
        manufacturingCharge: cart.itemsCost || 0,
        shippingCharge: cart.shippingCost || 0,
        packingCharge: cart.packingAndPackagingCost || 0,
        manufacturingTaxCharge: manufacturingTaxCharge,
        shippingTaxCharge: shippingTaxCharge,
        packingTaxCharge: packingTaxCharge
      },
      orderItems: orderItemsPayload
    }];

    // ── printNodeOrders[] with items + packages ────────────
    const printNodeOrdersPayload: any[] = [];
    // Per-PNOO shipping signature keyed on
    // `pnoo:<carrier>:<serviceLevel>:<facilityId>` — matches the
    // per-PNOO signature slot the server expects (see §4.1).
    const shippingSignatures: { [k: string]: string } = {};
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 1);

    for (const pnoo of pnooList) {
      const facilityId = pnoo.facilityId;

      // PrintNodeOrderItems — one per assigned cart item.
      const pnoiPayload: any[] = [];
      const itemsForFacility = (pnoo.items || []).filter(ci => ci && ci.quantity > 0);
      for (const ci of itemsForFacility) {
        pnoiPayload.push({
          product_ID: ci.getId(),         // resolves back to OrderItem after persistence
          book_ID: (ci.getBase && ci.getBase() && ci.getBase().book_ID) || 0,
          quantity: ci.quantity,
          initialQuantity: ci.quantity,
          weight: (ci.getWeight && ci.getWeight()) || 0,
          bookcost: ci.cost || 0
        });
      }

      // Package[] + OrderItemToPackage[] — fan out each
      // ShippingPackage by its `quantity` (physical box count) and
      // distribute item copies across the physical boxes so
      // `Σ oitp.quantity per product === item.quantity`. The server
      // may reshuffle if its authoritative packer disagrees, but
      // sending a consistent starting point lets it accept-as-is.
      const boxes = pnoo.shippingPackages || [];
      const layoutBoxCount = boxes.reduce((s, b) => s + (b.quantity || 1), 0) || 1;
      const perItemRemaining: { [productId: number]: number } = {};
      for (const ci of itemsForFacility) { perItemRemaining[ci.getId()] = ci.quantity; }
      const packagesPayload: any[] = [];
      let physicalBoxIndex = 0;
      for (const box of boxes) {
        const copies = box.quantity || 1;
        for (let copy = 0; copy < copies; copy++) {
          physicalBoxIndex++;
          const pkgOitps: any[] = [];
          for (const ci of itemsForFacility) {
            const remaining = perItemRemaining[ci.getId()] || 0;
            if (remaining <= 0) { continue; }
            const remainingBoxesForItem = layoutBoxCount - (physicalBoxIndex - 1);
            const takeQty = remainingBoxesForItem > 0
              ? Math.ceil(remaining / remainingBoxesForItem)
              : remaining;
            const putInThisBox = Math.min(takeQty, remaining);
            if (putInThisBox <= 0) { continue; }
            pkgOitps.push({
              product_ID: ci.getId(),
              printnode_ID: facilityId,
              quantity: putInThisBox
              // orderitem_ID + printnodeorderitem_ID + package_ID
              // are populated server-side during envelope
              // persistence (see §4.1 "orderitem_ID … is populated
              // by the server during persistence").
            });
            perItemRemaining[ci.getId()] = remaining - putInThisBox;
          }
          const packingCost = (box.packingCost != null)
            ? box.packingCost
            : ((typeof box.getPackingCost === 'function') ? box.getPackingCost() / copies : 0);
          packagesPayload.push({
            package: {
              printnode_ID: facilityId,
              carrier: cart.selectedCarrier || '',
              servicelevel: cart.selectedServiceLevel || '',
              packageType_ID: (box as any).packageTypeId || 0,
              weight: box.weight || 0,
              packageLabelStatus: 'INITIAL',
              shippingLabelStatus: 'INITIAL',
              shippingStatus: 'NONE',
              shippingCost: 0,        // server sets at label time
              packingCost: packingCost,
              serviceProviderPackageCharge: 0,
              facilityPackingCharge: 0
            },
            orderItemToPackages: pkgOitps
          });
        }
      }
      // Any residual per-item quantity gets dumped into the last box.
      if (packagesPayload.length > 0) {
        const last = packagesPayload[packagesPayload.length - 1];
        for (const ci of itemsForFacility) {
          const remainder = perItemRemaining[ci.getId()] || 0;
          if (remainder <= 0) { continue; }
          const existing = last.orderItemToPackages.find((o: any) => o.product_ID === ci.getId());
          if (existing) {
            existing.quantity += remainder;
          } else {
            last.orderItemToPackages.push({
              product_ID: ci.getId(),
              printnode_ID: facilityId,
              quantity: remainder
            });
          }
          perItemRemaining[ci.getId()] = 0;
        }
      }

      const pnoWeight = (pnoo.shippingPackages || []).reduce((s, b) => s + ((b.weight || 0) * (b.quantity || 1)), 0);
      let pnoQty = 0;
      for (const ci of itemsForFacility) { pnoQty += ci.quantity; }

      printNodeOrdersPayload.push({
        printNodeOrder: {
          printnode_ID: facilityId,
          carrier: cart.selectedCarrier || '',
          servicelevel: cart.selectedServiceLevel || '',
          title: '',
          firstname: (a.firstname || '').toUpperCase(),
          lastname: (a.lastname || '').toUpperCase(),
          company: (a.company || '').toUpperCase(),
          address1: a.address1 || '',
          address2: a.address2 || '',
          city: a.city || '',
          state: a.state || '',
          postalcode: a.postalcode || '',
          country: a.country || '',
          phone: cart.phone || '',
          phonecountry: cart.phoneCountry || '',
          email: a.email || '',
          dueDate: dueDate.toISOString(),
          inventoryType: cart.inventoryType,
          productionMethod: cart.inventoryType || 'PRINT_TO_ORDER',
          quantity: pnoQty,
          itemCount: itemsForFacility.length,
          weight: pnoWeight
        },
        printNodeOrderItems: pnoiPayload,
        packages: packagesPayload
      });

      // Shipping signature for this PNOO — captured on the cart
      // when the user picked the shipping choice (or when the S-3
      // rateShipping response landed).
      if (cart.selectedShippingRateSignature) {
        const key = `pnoo:${cart.selectedCarrier || ''}:${cart.selectedServiceLevel || ''}:${facilityId}`;
        shippingSignatures[key] = cart.selectedShippingRateSignature;
      }
    }

    // ── payment ────────────────────────────────────────────
    // The server routes on `payment.kind`; other fields are optional
    // hints. See `PlaceOrder.md` "Optional payment envelope fields".
    // `amount` is the client's view of the grand total (charges +
    // tax across all buckets). Server recomputes authoritatively but
    // requires the field to be present so it can cross-check before
    // committing the payment.
    const grandTotalAmount =
      (cart.itemsCost || 0)
      + (cart.shippingCost || 0)
      + (cart.packingAndPackagingCost || 0)
      + manufacturingTaxCharge
      + shippingTaxCharge
      + packingTaxCharge;
    let paymentPayload: any;
    if (cart.invoiceable && cart.payer === 'ORGANIZATION' && !cart.chargeProfile && !cart.cardNumberWithSpaces) {
      paymentPayload = { kind: 'INVOICE', amount: grandTotalAmount };
    } else if (cart.chargeProfile && cart.chargeProfile.ID > 0) {
      paymentPayload = {
        kind: 'CHARGEPROFILE',
        chargeprofile_ID: cart.chargeProfile.ID,
        amount: grandTotalAmount
      };
    } else if (cart.cardNumberWithSpaces) {
      // Raw card in the envelope only when the user opted NOT to
      // save it as a profile (otherwise the profile was already
      // tokenized in `preparePaymentThenPlaceOrder` and we're on
      // the CHARGEPROFILE branch above).
      paymentPayload = {
        kind: 'NEWCARD',
        saveAsChargeProfile: false,
        amount: grandTotalAmount,
        card: {
          firstName: cart.firstNamePayment || '',
          lastName: cart.lastNamePayment || '',
          cardNumber: (cart.cardNumberWithSpaces || '').split(' ').join(''),
          expMonth: cart.expMonth,
          expYear: cart.expYear,
          csc: cart.csc || '',
          address1: cart.address1Payment || cart.address.address1 || '',
          address2: cart.address2Payment || cart.address.address2 || '',
          city: cart.cityPayment || cart.address.city || '',
          state: cart.statePayment || cart.address.state || '',
          postalCode: cart.postalCodePayment || cart.address.postalcode || '',
          country: cart.countryPayment || cart.address.country || 'US'
        }
      };
    } else {
      paymentPayload = { kind: 'INVOICE', amount: grandTotalAmount };  // safest fallback
    }

    // ── signatures ─────────────────────────────────────────
    const productionRates: { [key: string]: string } = {};
    nonZeroCartItems.forEach((item, lineIdx) => {
      const sig = (item as any).productionRateSignature;
      if (sig) {
        productionRates[`item:${lineIdx}:${item.getId()}`] = sig;
      }
    });
    const signaturesPayload = {
      productionRates,
      facility: cart.facilitySignature || '',
      shipping: shippingSignatures,
      salesTax: cart.salesTaxSignature || ''
    };

    return {
      clientRequestId,
      order: orderPayload,
      orderDeliverTos: orderDeliverTosPayload,
      printNodeOrders: printNodeOrdersPayload,
      payment: paymentPayload,
      signatures: signaturesPayload
    };
  }

  /**
   * Real place-order submission via the single-envelope S-6 endpoint.
   * Sends {@link buildPlaceOrderEnvelope}; the server runs Phase A
   * (validate + signature-verify), Phase B (payment), and Phase C
   * (S3-queue + creator-Lambda handoff). On `ACCEPTED` the server has
   * already committed the payment (for non-INVOICE flows) and written
   * the envelope to S3; the actual DB row creation is asynchronous
   * and surfaces via the normal object-notification stream. The
   * client's job on `ACCEPTED` is to acknowledge, clear the cart, and
   * return the user to stage 1.
   *
   * There is no dry-run mode. Every call here really charges and really
   * persists; the server rejects an envelope carrying `dryRun` outright.
   */
  placeOrderViaEnvelope(cart: Cart): Observable<any> {
    if (!cart) {
      return throwError(() => new Error('placeOrderViaEnvelope: cart missing'));
    }
    const payload = this.buildPlaceOrderEnvelope(cart);
    const url = `${environment.apiBaseURL}order/placeorder`;
    this.messageService.add(`OrderService: placeOrderViaEnvelope clientRequestId=${payload.clientRequestId}`);
    return this.http.post<any>(url, payload, this.startupService.getHttpOptions()).pipe(
      catchError(err => {
        console.error('placeOrderViaEnvelope failed', err);
        this.log(`placeOrderViaEnvelope failed: ${err && err.message}`);
        return throwError(() => err);
      })
    );
  }

  /**
   * Generate a UUID v4 for the `clientRequestId` field on the place-order
   * envelope. Uses `crypto.randomUUID` when available, falls back to a
   * RFC4122-shaped string built from `Math.random` otherwise.
   */
  private generateClientRequestId(): string {
    try {
      const c: any = (typeof crypto !== 'undefined') ? crypto : undefined;
      if (c && typeof c.randomUUID === 'function') {
        return c.randomUUID();
      }
    } catch (_) { /* fall through to manual generator */ }
    // RFC4122 v4 with Math.random — not cryptographically strong but adequate
    // for an idempotency key the server treats opaquely.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
      const r = Math.random() * 16 | 0;
      const v = ch === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Place an order from the cart. Creates:
   *   - 1 Order
   *   - 1 OrderDeliverTo (with OrderItems for each non-zero cart item)
   *   - 1 PrintNodeOrder per ship-from facility (with PrintNodeOrderItems)
   *
   * The cart is expected to have a selectedOrderOption with one or more
   * PrintNodeOrderOptions. Each PrintNodeOrderOption maps to a single
   * PrintNodeOrder; its items map to PrintNodeOrderItems.
   */
  placeOrder(cart: Cart): Observable<Order> {
    if (!cart || !cart.selectedOrderOption) {
      return throwError(() => new Error('Cart has no selectedOrderOption'));
    }
    cart.placeOrderStatus = 'PLACING';

    const order = new Order(undefined);
    order.payer = cart.payer;
    order.org_ID = Number(this.startupService.selectedRoleId);
    const d = new Date();
    order.customerorderid = `Order-${d.getMonth() + 1}.${d.getDate()}.${d.getFullYear()}.${Math.floor(Math.random() * 1000)}`;
    order.inventoryType = cart.inventoryType;
    if (cart.purchaseOrder) { order.purchaseOrder = cart.purchaseOrder; }
    if (cart.purchaseOrderPrivate) { order.purchaseOrder_private = cart.purchaseOrderPrivate; }
    // Attach the saved ChargeProfile (token) when one has been selected/created.
    // Server uses chargeprofile_ID to charge against the stored gateway token;
    // when 0/missing it falls back to inline raw card fields or invoiced billing.
    if (cart.chargeProfile && cart.chargeProfile.ID > 0) {
      order.chargeprofile_ID = cart.chargeProfile.ID;
    }

    return this.addOrder(order).pipe(
      switchMap(createdOrder => {
        if (!createdOrder) {
          cart.placeOrderStatus = 'ERROR';
          return throwError(() => new Error('Failed to create Order'));
        }
        return this.createOrderDeliverToFromCart(cart, createdOrder).pipe(
          switchMap(createdODT => {
            if (!createdODT) {
              cart.placeOrderStatus = 'ERROR';
              return throwError(() => new Error('Failed to create OrderDeliverTo'));
            }
            return this.createPrintNodeOrdersFromCart(cart, createdOrder, createdODT).pipe(
              map(() => {
                cart.placeOrderStatus = 'SUCCESS';
                return createdOrder;
              })
            );
          })
        );
      }),
      catchError(err => {
        cart.placeOrderStatus = 'ERROR';
        console.error('placeOrder failed', err);
        return throwError(() => err);
      })
    );
  }

  private createOrderDeliverToFromCart(cart: Cart, order: Order): Observable<OrderDeliverTo> {
    const odt = new OrderDeliverTo(undefined);
    odt.order_ID = order.ID;
    odt.inventoryType = order.inventoryType;
    odt.title = '';
    odt.firstname = (cart.address.firstname || '').toUpperCase();
    odt.lastname = (cart.address.lastname || '').toUpperCase();
    odt.company = (cart.address.company || '').toUpperCase();
    odt.address1 = cart.address.address1 || '';
    odt.address2 = cart.address.address2 || '';
    odt.city = cart.address.city || '';
    odt.state = cart.address.state || '';
    odt.postalcode = cart.address.postalcode || '';
    odt.country = cart.address.country || '';
    odt.phone = cart.phone || '';
    odt.phonecountry = cart.phoneCountry || '';
    odt.email = cart.address.email || '';
    odt.carrier = cart.selectedCarrier || '';
    odt.servicelevel = cart.selectedServiceLevel || '';
    odt.addressValidationStatus = cart.addressStatus === 'VALIDATED' ? 'VALID'
      : cart.addressStatus === 'MANUAL_OVERRIDE' ? 'MANUAL_OVERRIDE' : 'PENDING';

    // Aggregate cart items into OrderItems (one per product/draft id, summing quantities).
    const byId: { [id: number]: OrderItem } = {};
    for (const cartItem of (cart.items || [])) {
      if (!cartItem || cartItem.quantity <= 0) { continue; }
      const id = cartItem.getId();
      let oi = byId[id];
      if (!oi) {
        oi = new OrderItem(undefined);
        oi.product_ID = id;
        oi.quantity = 0;
        oi.cost = 0;
        byId[id] = oi;
        odt.orderItems.push(oi);
      }
      oi.quantity += cartItem.quantity;
      oi.cost = (oi.cost || 0) + (cartItem.cost || 0);
    }

    // PF-13: this used to `catchError(... of(undefined))`, which threw away
    // the server's explanation and left `placeOrder` to report the generic
    // "Failed to create OrderDeliverTo". The server's own sentence names the
    // product, the quantity or the set that was refused, so it is propagated
    // untouched and surfaces in the cart's error toast.
    return this.orderDeliverToService.addOrderDeliverTo(odt);
  }

  /**
   * Create one PrintNodeOrder per PrintNodeOrderOption in the cart's
   * selectedOrderOption. Each PrintNodeOrder receives PrintNodeOrderItems
   * for the items assigned to that facility.
   */
  private createPrintNodeOrdersFromCart(cart: Cart, order: Order, odt: OrderDeliverTo): Observable<PrintNodeOrder[]> {
    const orderOption = cart.selectedOrderOption;
    if (!orderOption || !orderOption.printNodeOrderOptions || orderOption.printNodeOrderOptions.length === 0) {
      return of([]);
    }
    const observables: Observable<PrintNodeOrder>[] = [];
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 1);

    for (const pnoo of orderOption.printNodeOrderOptions) {
      const pno = new PrintNodeOrder(undefined);
      pno.org_ID = Number(this.startupService.selectedRoleId);
      pno.order_ID = order.ID;
      pno.orderdeliverto_ID = odt.ID;
      pno.printnode_ID = pnoo.facilityId;
      pno.address1 = odt.address1;
      pno.address2 = odt.address2;
      pno.city = odt.city;
      pno.state = odt.state;
      pno.postalcode = odt.postalcode;
      pno.country = odt.country;
      pno.title = odt.title;
      pno.firstname = odt.firstname;
      pno.lastname = odt.lastname;
      pno.company = odt.company;
      pno.carrier = odt.carrier;
      pno.servicelevel = odt.servicelevel;
      pno.phone = odt.phone;
      pno.phonecountry = odt.phonecountry;
      pno.email = odt.email;
      pno.dueDate = dueDate;
      pno.inventoryType = order.inventoryType;
      pno.quantity = 0;

      // Build PrintNodeOrderItems for each item assigned to this facility.
      pno['printNodeOrderItems'] = [];
      for (const cartItem of (pnoo.items || [])) {
        if (!cartItem || cartItem.quantity <= 0) { continue; }
        const pnoi = new PrintNodeOrderItem(undefined, pno);
        pnoi.book_ID = (cartItem.getBase && cartItem.getBase() && cartItem.getBase().book_ID) || 0;
        pnoi.quantity = cartItem.quantity;
        pnoi.initialQuantity = cartItem.quantity;
        pnoi.weight = cartItem.getWeight ? cartItem.getWeight() : 0;
        pnoi.bookcost = cartItem.cost || 0;
        // Match orderitem_ID by product id once OrderItems are persisted (server links them).
        const matchingOI = (odt.orderItems || []).find(oi => oi.product_ID === cartItem.getId());
        pnoi.orderitem_ID = matchingOI ? matchingOI.ID : 0;
        pno['printNodeOrderItems'].push(pnoi);
        pno.quantity += cartItem.quantity;
      }

      observables.push(this.printNodeOrderService.addItem(pno).pipe(
        catchError(err => {
          console.error('createPrintNodeOrder failed', err);
          return of(undefined as PrintNodeOrder);
        })
      ));
    }

    return forkJoin(observables);
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T> (operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      this.loadingState = 'INITIAL';

      // TODO: send the error to remote logging infrastructure
      console.error(error, operation); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
  private log(message: string) {
    this.messageService.add('OrderService: ' + message);
  }

}
